import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Trial-on-402 (Oct 2026 release, behind the `trial_on_402` switch).
 * lib/api.js dispatches "bm:trial-offer" whenever a 402 response carries
 * `trial_offer: true` — this modal catches it and offers the one-time
 * free Pro week at the exact moment the captain wanted a Pro feature.
 */
export default function TrialOfferModal() {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        const onOffer = () => setOpen(true);
        window.addEventListener("bm:trial-offer", onOffer);
        return () => window.removeEventListener("bm:trial-offer", onOffer);
    }, []);

    if (!open) return null;

    const start = async () => {
        setBusy(true);
        setErr("");
        try {
            await api.post("/billing/start-402-trial");
            setDone(true);
            setTimeout(() => window.location.reload(), 1600);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not start your Pro week.");
            setBusy(false);
        }
    };

    return createPortal(
        <div
            data-testid="trial-offer-modal"
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "grid", placeItems: "center", padding: 20, zIndex: 140 }}
            onClick={() => !busy && setOpen(false)}
        >
            <div className="bm-card" style={{ padding: 28, maxWidth: 420, width: "100%", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                <Sparkles size={26} color="var(--bm-gold)" style={{ marginBottom: 10 }} />
                {done ? (
                    <>
                        <h3 className="bm-serif" style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 500 }}>Pro unlocked ⚓</h3>
                        <p style={{ color: "var(--bm-text-muted)", fontSize: 14, margin: 0 }}>
                            Enjoy 7 days of Pro — reloading…
                        </p>
                    </>
                ) : (
                    <>
                        <h3 className="bm-serif" style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 500 }}>That's a Pro feature — try it free</h3>
                        <p style={{ color: "var(--bm-text-muted)", fontSize: 14, lineHeight: 1.6, margin: "0 0 6px" }}>
                            Unlock everything in Pro for the next 7 days — no card, no commitment.
                            One-time offer, exactly when you need it.
                        </p>
                        {err && <p data-testid="trial-offer-error" style={{ color: "var(--bm-bad, #f87171)", fontSize: 13, margin: "6px 0 0" }}>{err}</p>}
                        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 18 }}>
                            <button data-testid="trial-offer-dismiss-btn" className="bm-btn bm-btn-ghost" onClick={() => setOpen(false)} disabled={busy} style={{ padding: "10px 16px" }}>
                                Maybe later
                            </button>
                            <button data-testid="trial-offer-start-btn" className="bm-btn bm-btn-gold" onClick={start} disabled={busy} style={{ padding: "10px 20px" }}>
                                {busy ? "Unlocking…" : "Start my free Pro week"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
}
